import { Dialog, DialogPanel, DialogBackdrop, DialogTitle, Transition } from '@headlessui/react'
import { faCircleCheck, faSpinner, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useRef, useState } from 'react'

import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import useAuth from '../../../../hooks/useAuth'

const SUBMISSION_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.'

const ReferralModal = ({ openReferral, setOpenReferral }) => {
  const { auth } = useAuth()

  const axios = useAxiosPrivate()

  const policyData = JSON.parse(localStorage.getItem('policyData') || '{}')

  const currentSchoolArray = auth?.schools?.filter((school) => school.id === policyData?.policyable_id)
  const currentschoolData = currentSchoolArray?.[0] ?? {}

  const [schoolName, setSchoolName] = useState('')
  const [schoolPostcode, setSchoolPostcode] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [positionHeld, setPositionHeld] = useState('')
  const [loading, setLoading] = useState(false)
  const [showThanks, setShowThanks] = useState(false)
  const [submissionError, setSubmissionError] = useState('')
  const closeTimer = useRef(null)

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const handleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = null
    setShowThanks(false)
    setSubmissionError('')
    setOpenReferral(false)
  }

  const resetInputs = () => {
    setSchoolName('')
    setSchoolPostcode('')
    setContactName('')
    setContactEmail('')
    setPositionHeld('')
  }

  const submitForm = async () => {
    setSubmissionError('')

    if (!auth?.user?.contact_id || !policyData?.policyable_type || !currentschoolData?.id) {
      setSubmissionError(SUBMISSION_ERROR_MESSAGE)
      return
    }

    setLoading(true)

    try {
      const params = {
        contact_id: auth.user.contact_id,
        org_type: policyData.policyable_type.toLowerCase().includes('academy') ? 'academy' : 'trust',
        referrable_id: currentschoolData.id,
        company_id: 0,
        details: {
          school_name: schoolName,
          school_postcode: schoolPostcode,
          contact_name: contactName,
          contact_email: contactEmail,
          contact_position: positionHeld,
          referrer_school_name: schoolName
        }
      }

      await axios.post('v1/staff-hub/referral/create', params)
      setShowThanks(true)
      resetInputs()

      closeTimer.current = setTimeout(() => {
        setShowThanks(false)
        setOpenReferral(false)
        closeTimer.current = null
      }, 2000)
    } catch (error) {
      console.error(error)
      setSubmissionError(SUBMISSION_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Transition show={openReferral} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-30" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-xl rounded-xl bg-white shadow-xl p-6 overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <DialogTitle className="text-lg font-semibold text-gray-800 text-center w-full">
                Exclusive Refer A School Offer for SAS Customers
              </DialogTitle>
              <button type="button" aria-label="Close referral form" className="text-gray-500 hover:text-gray-700" onClick={handleClose}>
                <FontAwesomeIcon icon={faXmarkCircle} className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            {!showThanks && (
              <>
                <p className="text-sm text-center mb-4">
                  <strong>
                    If you refer another school to us, we will give your school a digital voucher of your
                    choosing for £100! See full terms and conditions on our website at{' '}
                    <a
                      href="https://schooladvice.co.uk/refer-a-school"
                      className="text-blue-600 underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      schooladvice.co.uk/refer-a-school
                    </a>
                  </strong>
                </p>

                <p className="mb-4">Please enter the details of the school/academy you are referring.</p>

                {submissionError && (
                  <div role="alert" className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {submissionError}
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); submitForm() }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium">School Name</label>
                    <input type="text" className="w-full border rounded p-2" value={schoolName} onChange={e => setSchoolName(e.target.value)} disabled={loading} required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Postcode</label>
                    <input type="text" className="w-full border rounded p-2" value={schoolPostcode} onChange={e => setSchoolPostcode(e.target.value)} disabled={loading} required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Contact Name</label>
                    <input type="text" className="w-full border rounded p-2" value={contactName} onChange={e => setContactName(e.target.value)} disabled={loading} required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Contact Email</label>
                    <input type="email" className="w-full border rounded p-2" value={contactEmail} onChange={e => setContactEmail(e.target.value)} disabled={loading} required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Position Held</label>
                    <input type="text" className="w-full border rounded p-2" value={positionHeld} onChange={e => setPositionHeld(e.target.value)} disabled={loading} required />
                  </div>

                  <div className="text-center pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-64 py-2 bg-sas text-white font-semibold rounded hover:bg-sas-dark transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? (
                        <span className="inline-flex items-center gap-2">
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Sending Referral...
                        </span>
                      ) : 'Send Referral'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Thanks */}
            {showThanks && (
              <div role="status" aria-live="polite" className="text-center py-6">
                <FontAwesomeIcon icon={faCircleCheck} className="mb-4 text-5xl text-green-600" />
                <p className="text-lg font-semibold">Thank you for your referral!</p>
                <p className="mt-2 text-sm text-gray-600">This window will close automatically.</p>
              </div>
            )}
          </DialogPanel>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ReferralModal
